import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { track } from '../lib/posthog';
import {
  AirplaneTilt,
  ArrowUpRight,
  ArrowsLeftRight,
  Bank,
  Baseball,
  Basketball,
  Car,
  ChartLineUp,
  Coins,
  Cpu,
  CurrencyDollar,
  Diamond,
  FlagCheckered,
  Football,
  GameController,
  GlobeHemisphereWest,
  HandFist,
  MusicNotes,
  Package,
  Robot,
  ShareNetwork,
  SoccerBall,
  Star,
  Trophy,
  TShirt,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
// DEV-12658: inlined as a <style> element instead of joining the global
// stylesheet. global.css loads async (DEV-12090 media="print"), and React can
// mount before it applies; an unstyled hero would shift the LCP h1 that the
// pre-React shell already painted. The inline rules travel with the component.
import heroDiscoverCss from './hero-discover.css?raw';
import { afterBlockingWindow, POINTER_EVENTS } from '../lib/after-blocking-window';

/* DEV-12658: V3 homepage hero ("open discovery field").
 *
 * Desktop (>1024px): a clean canvas with a protected central message and
 * eighteen cards scattered around it the way the Luma reference does, in
 * several sizes for depth; the top row dissolves up behind the nav. Each card sits in a
 * glassy polaroid frame. Cards emerge from depth on first paint, float in
 * place continuously, and one at a time dissolve and come back as a different
 * topic, category or creator.
 *
 * There is no circle at rest. Hovering or focusing the CTA grows one out of
 * the button, and every hover brings the next theme (radar, aurora,
 * constellation, pulse). Leaving the CTA contracts it again.
 *
 * Tablet and mobile (<=1024px): the same message, then three rows of compact
 * pills drifting in alternating directions (Jon, 2026-09-14: "the pills do
 * nearly exactly the same job in 1/4 the space").
 *
 * The h1, eyebrow and subtitle never animate position or size: they are the
 * LCP paint and must match the pre-React shell (functions/data/home-shell.ts)
 * pixel for pixel. All motion respects prefers-reduced-motion. */

type Entity = {
  id: string;
  name: string;
  kind: 'category' | 'topic' | 'creator';
  /** App path without the locale prefix. */
  path: string;
  icon?: Icon;
  image?: string;
  handle?: string;
  /** Brand color the card is subtly tinted with (vendor colors, allowlisted). */
  tint: string;
};

const IMG = '/landing-assets/home-hero';

const ENTITIES: Entity[] = [
  { id: 'crypto', name: 'Crypto', kind: 'category', path: '/categories/crypto', icon: Coins, tint: '#F7931A' },
  { id: 'bitcoin', name: 'Bitcoin', kind: 'topic', path: '/topic/bitcoin-btc', image: `${IMG}/bitcoin.webp`, tint: '#F7931A' },
  { id: 'mkbhd', name: 'Marques Brownlee', kind: 'creator', path: '/creator/x:mkbhd', image: `${IMG}/mkbhd.webp`, handle: '@MKBHD · X', tint: '#E11D48' },
  { id: 'ai', name: 'AI', kind: 'category', path: '/categories/ai', icon: Robot, tint: '#8B5CF6' },
  { id: 'youtube', name: 'YouTube', kind: 'topic', path: '/topic/youtube', image: `${IMG}/youtube.webp`, tint: '#FF0000' },
  { id: 'mrbeast', name: 'MrBeast', kind: 'creator', path: '/creator/x:mrbeast', image: `${IMG}/mrbeast.webp`, handle: '@MrBeast · X', tint: '#2BB6E8' },
  { id: 'solana', name: 'Solana', kind: 'topic', path: '/topic/solana-sol', image: `${IMG}/solana.webp`, tint: '#9945FF' },
  { id: 'stocks', name: 'Stocks', kind: 'category', path: '/categories/stocks', icon: ChartLineUp, tint: '#16A34A' },
  { id: 'ethereum', name: 'Ethereum', kind: 'topic', path: '/topic/ethereum-eth', image: `${IMG}/ethereum.webp`, tint: '#627EEA' },
  { id: 'nfl', name: 'NFL', kind: 'category', path: '/categories/nfl', icon: Football, tint: '#D50A0A' },
  { id: 'tiktok', name: 'TikTok', kind: 'topic', path: '/topic/tiktok', image: `${IMG}/tiktok.webp`, tint: '#FE2C55' },
  { id: 'gaming', name: 'Gaming', kind: 'category', path: '/categories/gaming', icon: GameController, tint: '#7C3AED' },
  { id: 'x', name: 'X', kind: 'topic', path: '/topic/x', image: `${IMG}/x.svg`, tint: '#1D9BF0' },
  { id: 'finance', name: 'Finance', kind: 'category', path: '/categories/finance', icon: Bank, tint: '#0EA5E9' },
  { id: 'nba', name: 'NBA', kind: 'category', path: '/categories/nba', icon: Basketball, tint: '#F58426' },
  { id: 'musicians', name: 'Musicians', kind: 'category', path: '/categories/musicians', icon: MusicNotes, tint: '#EC4899' },
  { id: 'formula-1', name: 'Formula 1', kind: 'category', path: '/categories/formula-1', icon: FlagCheckered, tint: '#E10600' },
  { id: 'premier-league', name: 'Premier League', kind: 'category', path: '/categories/premier-league', icon: SoccerBall, tint: '#8B2E8A' },
  { id: 'technology-brands', name: 'Tech Brands', kind: 'category', path: '/categories/technology-brands', icon: Cpu, tint: '#3B82F6' },
  { id: 'travel-destinations', name: 'Travel', kind: 'category', path: '/categories/travel-destinations', icon: AirplaneTilt, tint: '#06B6D4' },
  { id: 'dogecoin', name: 'Dogecoin', kind: 'topic', path: '/topic/dogecoin-doge', image: `${IMG}/dogecoin.webp`, tint: '#C2A633' },
  { id: 'nvidia', name: 'NVIDIA', kind: 'topic', path: '/topic/nvidia', image: `${IMG}/nvidia.webp`, tint: '#76B900' },
  { id: 'tesla', name: 'Tesla', kind: 'topic', path: '/topic/tesla', image: `${IMG}/tesla.webp`, tint: '#E82127' },
  { id: 'xrp', name: 'XRP', kind: 'topic', path: '/topic/xrp-xrp', image: `${IMG}/xrp.webp`, tint: '#346AA9' },
  { id: 'cardano', name: 'Cardano', kind: 'topic', path: '/topic/cardano-ada', image: `${IMG}/cardano.webp`, tint: '#0033AD' },
  { id: 'celebrities', name: 'Celebrities', kind: 'category', path: '/categories/celebrities', icon: Star, tint: '#F59E0B' },
  { id: 'countries', name: 'Countries', kind: 'category', path: '/categories/countries', icon: GlobeHemisphereWest, tint: '#10B981' },
  { id: 'social-networks', name: 'Social Networks', kind: 'category', path: '/categories/social-networks', icon: ShareNetwork, tint: '#6366F1' },
  { id: 'automotive-brands', name: 'Automotive', kind: 'category', path: '/categories/automotive-brands', icon: Car, tint: '#64748B' },
  { id: 'luxury-brands', name: 'Luxury Brands', kind: 'category', path: '/categories/luxury-brands', icon: Diamond, tint: '#C9A227' },
  { id: 'fashion-brands', name: 'Fashion', kind: 'category', path: '/categories/fashion-brands', icon: TShirt, tint: '#DB2777' },
  { id: 'mlb', name: 'MLB', kind: 'category', path: '/categories/mlb', icon: Baseball, tint: '#1D4ED8' },
  { id: 'world-cup', name: 'World Cup', kind: 'category', path: '/categories/world-cup', icon: Trophy, tint: '#16A34A' },
  { id: 'currencies', name: 'Currencies', kind: 'category', path: '/categories/currencies', icon: CurrencyDollar, tint: '#22C55E' },
  { id: 'products', name: 'Products', kind: 'category', path: '/categories/products', icon: Package, tint: '#F97316' },
  { id: 'exchanges', name: 'Exchanges', kind: 'category', path: '/categories/exchanges', icon: ArrowsLeftRight, tint: '#0891B2' },
  { id: 'ufc', name: 'UFC', kind: 'category', path: '/categories/ufc', icon: HandFist, tint: '#D20A0A' },
];

const ENTITY_BY_ID = new Map(ENTITIES.map((e) => [e.id, e]));

/* Card field, Luma-style. Coordinates are the card's top-left in the 1312px
 * stage at 1440. Every card stays inside the stage horizontally; y may be
 * negative, which runs the card up behind the nav where it dissolves. `s` scales
 * the 190px base card for depth. `anchor` keeps side cards hugging their edge
 * as the stage narrows; centre cards hold their offset from the centre line.
 * The message owns roughly x 380–932, y 120–520: nothing is placed there.
 * `wide` cards only show at 1280px and up. */
type Spot = {
  initial: string;
  x: number;
  y: number;
  s: number;
  anchor: 'left' | 'right' | 'center';
  wide?: boolean;
};

const BASE_W = 190;
const STAGE_W = 1312;

const SPOTS: Spot[] = [
  { initial: 'crypto', x: 470, y: -40, s: 1.12, anchor: 'center' },
  { initial: 'bitcoin', x: 212, y: 64, s: 1.0, anchor: 'left' },
  { initial: 'ai', x: 790, y: 0, s: 0.82, anchor: 'center', wide: true },
  { initial: 'mrbeast', x: 1040, y: 48, s: 1.18, anchor: 'right' },
  { initial: 'tiktok', x: 0, y: 262, s: 0.86, anchor: 'left' },
  { initial: 'stocks', x: 150, y: 372, s: 0.9, anchor: 'left', wide: true },
  { initial: 'youtube', x: 1124, y: 250, s: 0.96, anchor: 'right' },
  { initial: 'nvidia', x: 930, y: 462, s: 0.82, anchor: 'right', wide: true },
  { initial: 'mkbhd', x: 34, y: 560, s: 1.24, anchor: 'left' },
  { initial: 'nfl', x: 1098, y: 566, s: 1.1, anchor: 'right' },
  { initial: 'solana', x: 338, y: 700, s: 1.02, anchor: 'left' },
  { initial: 'finance', x: 790, y: 740, s: 1.2, anchor: 'center' },
  { initial: 'ethereum', x: 0, y: 790, s: 0.94, anchor: 'left', wide: true },
  { initial: 'gaming', x: 1148, y: 800, s: 0.86, anchor: 'right' },
  { initial: 'dogecoin', x: 580, y: 650, s: 0.74, anchor: 'center' },
  { initial: 'formula-1', x: 10, y: 40, s: 0.8, anchor: 'left', wide: true },
  { initial: 'x', x: 8, y: 452, s: 0.72, anchor: 'left' },
  { initial: 'premier-league', x: 1170, y: 432, s: 0.74, anchor: 'right', wide: true },
];

/* Floating in space: two independent loops per card (a vertical bob with a
 * slow tilt, and a horizontal sway with a breath of scale) whose periods never
 * line up. Deterministic so every render agrees. */
const FLOAT = SPOTS.map((_, i) => {
  const dir = i % 2 === 0 ? 1 : -1;
  return {
    yd: 12.5 + ((i * 37) % 60) / 10,
    xd: 17 + ((i * 53) % 80) / 10,
    delay: -((i * 29) % 140) / 10,
    y: dir * (11 + ((i * 7) % 8)),
    x: -dir * (8 + ((i * 5) % 8)),
    r: dir * (0.8 + ((i * 3) % 9) / 10),
  };
});

/* Each hover brings the next circle. */
const BLOOM_THEMES = ['radar', 'aurora', 'constellation', 'pulse'] as const;

const PILL_ROWS: string[][] = [
  ['solana', 'bitcoin', 'crypto', 'mkbhd', 'ai', 'formula-1'],
  ['tiktok', 'stocks', 'mrbeast', 'ethereum', 'musicians', 'nba'],
  ['nfl', 'youtube', 'gaming', 'x', 'finance', 'premier-league'],
];

const ROTATE_EVERY_MS = 2600;
const EXIT_MS = 700;

const SUPPORTED_APP_LOCALES = new Set(['en', 'es', 'de', 'fr', 'zh']);

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true,
  );
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

const EntityGlyph: React.FC<{ entity: Entity; size: number; iconSize: number; className: string }> = ({
  entity,
  size,
  iconSize,
  className,
}) => {
  if (entity.icon) {
    const Glyph = entity.icon;
    return (
      <span className={`${className} ${className}--icon`} style={{ width: size, height: size }} aria-hidden="true">
        <Glyph size={iconSize} />
      </span>
    );
  }
  // DEV-12684: lazy-load entity images and deprioritize them to reduce initial payload
  return <img className={className} src={entity.image} alt="" width={size} height={size} decoding="async" loading="lazy" fetchPriority="low" />;
};

/* Constellation: fixed nodes and the links between them (0–1000 viewBox). */
const STAR_NODES: [number, number][] = [
  [210, 300], [330, 180], [480, 240], [640, 150], [790, 290], [860, 470],
  [720, 620], [560, 760], [380, 690], [200, 560], [470, 430], [640, 420],
];
const STAR_LINKS: [number, number][] = [
  [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8], [8, 9], [9, 0],
  [2, 10], [10, 11], [11, 4], [10, 8], [11, 6],
];

const BloomArt: React.FC = () => (
  <>
    <div className="hdv-art hdv-art--radar">
      <span className="hdv-radar-rings" />
      <span className="hdv-radar-sweep" />
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className={`hdv-radar-blip hdv-radar-blip--${i + 1}`} />
      ))}
    </div>
    <div className="hdv-art hdv-art--aurora">
      <span className="hdv-aurora-blobs" />
    </div>
    <div className="hdv-art hdv-art--constellation">
      <span className="hdv-constellation-grid" />
      <svg className="hdv-constellation-map" viewBox="0 0 1000 1000" preserveAspectRatio="xMidYMid slice">
        {STAR_LINKS.map(([a, b], i) => (
          <line
            key={i}
            x1={STAR_NODES[a][0]}
            y1={STAR_NODES[a][1]}
            x2={STAR_NODES[b][0]}
            y2={STAR_NODES[b][1]}
            style={{ animationDelay: `${-i * 0.7}s` }}
          />
        ))}
        {STAR_NODES.map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r={i % 3 === 0 ? 7 : 4.5} style={{ animationDelay: `${-i * 0.45}s` }} />
        ))}
      </svg>
    </div>
    <div className="hdv-art hdv-art--pulse">
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className="hdv-pulse-ring" style={{ animationDelay: `${i * 0.9}s` }} />
      ))}
    </div>
  </>
);

type SpotState = { id: string; leaving: boolean; key: number };

export type HeroDiscoverProps = {
  eyebrow: string;
  headline: string;
  subtitle: string;
  ctaLabel: string;
  /** App path without the locale prefix, e.g. "/home". */
  ctaPath: string;
};

export const HeroDiscover: React.FC<HeroDiscoverProps> = ({ eyebrow, headline, subtitle, ctaLabel, ctaPath }) => {
  const { i18n } = useTranslation();
  const locale = SUPPORTED_APP_LOCALES.has(i18n.language) ? i18n.language : 'en';
  const appHref = useCallback((path: string) => `/${locale}${path}`, [locale]);

  const reducedMotion = usePrefersReducedMotion();
  const [bloom, setBloom] = useState(false);
  const [theme, setTheme] = useState(0);
  const [spots, setSpots] = useState<SpotState[]>(() => SPOTS.map((s, i) => ({ id: s.initial, leaving: false, key: i })));
  // DEV-12684: defer card rotation past the blocking window to reduce main-thread work
  const [rotationEnabled, setRotationEnabled] = useState(false);
  const spotsRef = useRef(spots);
  spotsRef.current = spots;
  const bloomRef = useRef(false);
  bloomRef.current = bloom;
  const hovered = useRef<number | null>(null);
  const lastSpot = useRef(-1);
  const visibleRef = useRef(true);
  const rootRef = useRef<HTMLDivElement>(null);
  const bloomedOnce = useRef(false);

  // Split "The Social Media Sentiment Layer" into two balanced lines
  // ("The Social Media" / "Sentiment Layer"), like the Paper comps. Any other
  // CMS headline renders as one wrapping line.
  const [lineOne, lineTwo] = useMemo(() => {
    const marker = headline.search(/ (?:Sentiment|Intelligence) Layer/);
    return marker > 0 ? [headline.slice(0, marker), headline.slice(marker + 1)] : [headline, ''];
  }, [headline]);

  // The comps break the subtitle after its first clause.
  const subtitleParts = useMemo(() => {
    const comma = subtitle.search(/[,，]\s*/);
    if (comma <= 0) return [subtitle, ''];
    const rest = subtitle.slice(comma + 1).trimStart();
    return [subtitle.slice(0, comma + 1), rest];
  }, [subtitle]);

  // DEV-12684: enable rotation after the blocking window or first pointer interaction.
  useEffect(() => {
    return afterBlockingWindow(() => setRotationEnabled(true), 4000, POINTER_EVENTS);
  }, []);

  // Pause rotation while the hero is off screen.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => {
      visibleRef.current = entry.isIntersecting;
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Rotation: every tick one card dissolves and returns as an unseen entity.
  // DEV-12684: disabled until after blocking window to reduce INP/main-thread cost.
  useEffect(() => {
    if (reducedMotion || !rotationEnabled) return;
    let swapTimer: number | undefined;
    const tick = () => {
      if (document.hidden || !visibleRef.current || bloomRef.current) return;
      const current = spotsRef.current;
      const onScreen = new Set(current.map((s) => s.id));
      const candidates = SPOTS.map((_, i) => i).filter(
        (i) => i !== hovered.current && i !== lastSpot.current && !current[i].leaving,
      );
      if (candidates.length === 0) return;
      const index = candidates[Math.floor(Math.random() * candidates.length)];
      const pool = ENTITIES.filter((e) => !onScreen.has(e.id));
      if (pool.length === 0) return;
      const next = pool[Math.floor(Math.random() * pool.length)];
      lastSpot.current = index;

      setSpots((latest) => latest.map((s, i) => (i === index ? { ...s, leaving: true } : s)));
      swapTimer = window.setTimeout(() => {
        setSpots((latest) =>
          latest.map((s, i) => (i === index ? { id: next.id, leaving: false, key: s.key + SPOTS.length } : s)),
        );
      }, EXIT_MS);
    };
    const interval = window.setInterval(tick, ROTATE_EVERY_MS);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(swapTimer);
    };
  }, [reducedMotion, rotationEnabled]);

  // What the visitor is actually looking at. The hero is two different designs
  // behind one breakpoint (hero-discover.css): floating cards above 1024px,
  // scrolling pills at and below it. Device type does not tell them apart — a
  // narrow desktop window gets pills — so the layout is reported as itself.
  const heroLayout = () =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches ? 'pills' : 'cards';

  // The hero had no events of its own: its clicks were only recoverable from
  // autocapture by CSS class, which counted the desktop cards and silently
  // missed the mobile pills. One event for every way out of the hero, told
  // apart by `target`, so "did the hero send them on" is a single funnel step.
  // `bloom_theme` rides along because the CTA shows a different theme on each
  // hover and nothing else can say whether any of them pulls harder.
  const reportHeroClick = (
    target: 'cta' | 'card' | 'pill',
    details: Record<string, unknown>,
  ) => {
    track('home_hero_click', { target, layout: heroLayout(), locale, ...details });
  };

  // Reports and nothing else: the anchor's href still does the navigating, so
  // cmd/ctrl-click and "open in new tab" keep working. Named rather than
  // inline because HomePage.hero.test.ts reads the tag up to its first `>`.
  const reportCtaClick = () =>
    reportHeroClick('cta', {
      label: ctaLabel,
      path: ctaPath,
      bloom_theme: bloomedOnce.current ? BLOOM_THEMES[theme] : null,
    });

  // The denominator, with the layout the pageview cannot know.
  useEffect(() => {
    track('home_hero_viewed', { layout: heroLayout(), locale });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount
  }, []);

  const openBloom = () => {
    if (bloomRef.current) return;
    // The first hover shows the first theme; each later hover the next one.
    if (bloomedOnce.current) setTheme((t) => (t + 1) % BLOOM_THEMES.length);
    bloomedOnce.current = true;
    setBloom(true);
  };
  const closeBloom = () => setBloom(false);

  return (
    <div
      className="hdv"
      data-hero-discover=""
      data-bloom={bloom ? 'true' : 'false'}
      data-bloom-theme={BLOOM_THEMES[theme]}
      ref={rootRef}
    >
      <style>{heroDiscoverCss}</style>
      <div className="hdv-stage">
        <div className="hdv-halo" aria-hidden="true" />
        <div className="hdv-burst-clip" aria-hidden="true">
          <div className="hdv-burst">
            <BloomArt />
          </div>
        </div>

        <div className="hdv-message">
          <p className="hdv-eyebrow">{eyebrow}</p>
          <h1 className="hdv-title" dir="auto">
            {lineOne}
            {lineTwo && (
              <>
                {/* The space keeps the headline one phrase in textContent
                    ("The Social Media Sentiment Layer") for crawlers, screen
                    readers and the marketing SPA guard; a bare <br /> joined
                    it as "MediaSentiment". Trailing space before a line
                    break does not render. */}
                {" "}
                <br />
                {lineTwo}
              </>
            )}
          </h1>
          <p className="hdv-subtitle">
            {subtitleParts[0]}
            {subtitleParts[1] && (
              <>
                <br />
                {subtitleParts[1]}
              </>
            )}
          </p>
          <a
            className="hdv-cta"
            href={appHref(ctaPath)}
            onPointerEnter={openBloom}
            onPointerLeave={closeBloom}
            onFocus={openBloom}
            onBlur={closeBloom}
            onClick={reportCtaClick}
          >
            <span>{ctaLabel}</span>
            <ArrowUpRight size={16} weight="bold" aria-hidden="true" />
          </a>
        </div>

        {/* Desktop field */}
        <div className="hdv-field">
          <ul className="hdv-field-inner" aria-label="Popular on LunarCrush">
            {SPOTS.map((spot, i) => {
              const state = spots[i];
              const entity = ENTITY_BY_ID.get(state.id)!;
              const float = FLOAT[i];
              const w = BASE_W * spot.s;
              // Bloom pushes every card away from the CTA.
              const dx = spot.x + w / 2 - STAGE_W / 2;
              const dy = spot.y + 80 - 435; // from the CTA, in comp coordinates
              const len = Math.hypot(dx, dy) || 1;
              const style = {
                '--hdv-left': `${(spot.x / STAGE_W) * 100}%`,
                '--hdv-right': `${((STAGE_W - spot.x - w) / STAGE_W) * 100}%`,
                '--hdv-cx': `${spot.x - STAGE_W / 2}px`,
                // The message and the card cloud are both centred in the stage
                // (the cloud spans y -40…930 in comp coordinates, centre ~400).
                // --hdv-spread tightens the cloud on short viewports.
                top: `calc(50% + ${spot.y - 400}px * var(--hdv-spread))`,
                '--hdv-s': spot.s,
                '--hdv-enter-delay': `${150 + i * 85}ms`,
                '--hdv-float-yd': `${float.yd}s`,
                '--hdv-float-xd': `${float.xd}s`,
                '--hdv-float-delay': `${float.delay}s`,
                '--hdv-float-y': `${float.y}px`,
                '--hdv-float-x': `${float.x}px`,
                '--hdv-float-r': `${float.r}deg`,
                '--hdv-push-x': `${(dx / len) * 34}px`,
                '--hdv-push-y': `${(dy / len) * 26}px`,
              } as React.CSSProperties;
              return (
                <li
                  key={i}
                  className={`hdv-spot hdv-spot--${spot.anchor}${spot.wide ? ' hdv-spot--wide' : ''}`}
                  style={style}
                  onPointerEnter={() => {
                    hovered.current = i;
                  }}
                  onPointerLeave={() => {
                    if (hovered.current === i) hovered.current = null;
                  }}
                >
                  <div className="hdv-scale">
                    <div className="hdv-float">
                      <div className="hdv-drift">
                        <a
                          key={state.key}
                          className={`hdv-card hdv-card--${entity.kind}${state.key >= SPOTS.length ? ' is-swapped' : ''}${
                            state.leaving ? ' is-leaving' : ''
                          }`}
                          href={appHref(entity.path)}
                          style={{ '--hdv-tint': entity.tint } as React.CSSProperties}
                          onClick={() =>
                            reportHeroClick('card', {
                              entity: entity.id,
                              entity_kind: entity.kind,
                              path: entity.path,
                              position: i,
                            })
                          }
                        >
                          <span className="hdv-card-face">
                            {entity.kind === 'category' ? (
                              <span className="hdv-card-top hdv-card-top--end">
                                <EntityGlyph entity={entity} size={28} iconSize={28} className="hdv-card-icon" />
                              </span>
                            ) : (
                              <span className="hdv-card-top">
                                <EntityGlyph entity={entity} size={42} iconSize={24} className="hdv-card-avatar" />
                              </span>
                            )}
                            <span className="hdv-card-body">
                              <span className="hdv-card-name">{entity.name}</span>
                              {entity.handle && <span className="hdv-card-handle">{entity.handle}</span>}
                            </span>
                          </span>
                        </a>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Tablet + mobile pills */}
        <div className="hdv-pills">
          {PILL_ROWS.map((row, r) => (
            <div key={r} className={`hdv-pill-row hdv-pill-row--${r + 1}`}>
              <div className="hdv-pill-track">
                {[0, 1].map((copy) =>
                  row.map((id) => {
                    const entity = ENTITY_BY_ID.get(id)!;
                    const clone = copy === 1;
                    return (
                      <a
                        key={`${copy}-${id}`}
                        className={`hdv-pill${entity.kind === 'category' ? ' hdv-pill--category' : ''}`}
                        href={appHref(entity.path)}
                        aria-hidden={clone ? 'true' : undefined}
                        tabIndex={clone ? -1 : undefined}
                        style={{ '--hdv-tint': entity.tint } as React.CSSProperties}
                        onClick={() =>
                          reportHeroClick('pill', {
                            entity: entity.id,
                            entity_kind: entity.kind,
                            path: entity.path,
                            position: r,
                          })
                        }
                      >
                        <EntityGlyph entity={entity} size={32} iconSize={18} className="hdv-pill-glyph" />
                        <span>{entity.name}</span>
                      </a>
                    );
                  }),
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HeroDiscover;
